import React, { useState } from 'react';
import Table from "src/componets/Table";
import { useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { AvailablePhoneNumberInterface } from "src/store/callrecorder/availablePhoneNumbersSlice";
import PricingPlansModal from "./_PricingPlansModal";
import SmsPackagesModal from "./_SmsPackagesModal";
import { Button } from "src/componets/Buttons";

import { Tag, Tooltip } from "antd";
import {
  PhoneOutlined,
  MessageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined
} from "@ant-design/icons";

const _ListTable = () => {
  const ids = useAppSelector(state => state.callrecorder.availablePhoneNumbers.ids);
  const records = useAppSelector(state => state.callrecorder.availablePhoneNumbers.entities);
  const data = ids ? ids.map(id => records[id]) : [];

  const [pricingUid, setPricingUid] = useState<string | null>(null);
  const [smsUid, setSmsUid] = useState<string | null>(null);
  const [modalNumber, setModalNumber] = useState('');

  const openPricingModal = (uid: string, number: number) => {
    setModalNumber(`+${number}`);
    setPricingUid(uid);
  };

  const openSmsModal = (uid: string, number: number) => {
    setModalNumber(`+${number}`);
    setSmsUid(uid);
  };

  const renderCapability = (value: boolean, domesticOnly?: boolean) => {
    if (!value) return <CloseCircleOutlined style={{ color: '#d9d9d9' }} />;
    if (domesticOnly) return <CheckCircleOutlined style={{ color: '#faad14' }} />;
    return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
  }

  const renderNumberType = (type: string) => {
    const colors: Record<string, string> = {
      local: 'blue',
      mobile: 'green',
      national: 'orange',
      tollfree: 'purple'
    };
    return <Tag color={colors[type] || 'default'}>{type}</Tag>;
  }

  const callTooltip = (label: string, domesticOnly: boolean) =>
    domesticOnly ? `${label} (domestic only)` : label;

  const columns: ColumnsType<AvailablePhoneNumberInterface> = [
    {
      key: 'number',
      title: 'Number',
      render: (_, { number }) => <strong>+{number}</strong>,
      width: 160
    },

    {
      key: 'phone_number_type',
      title: 'Type',
      render: (_, { phone_number_type }) => renderNumberType(phone_number_type),
      width: 100
    },

    {
      key: 'country_code',
      title: 'Country',
      dataIndex: 'country_code',
      width: 80
    },

    {
      key: 'capabilities',
      title: 'Capabilities',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Tooltip title={callTooltip('Inbound Call', record.domestic_voice_only)}>
            <span><PhoneOutlined /> {renderCapability(record.inbound_call, record.domestic_voice_only)}</span>
          </Tooltip>
          <Tooltip title={callTooltip('Outgoing Call', record.domestic_voice_only)}>
            <span><PhoneOutlined rotate={135} /> {renderCapability(record.outgoing_call, record.domestic_voice_only)}</span>
          </Tooltip>
          <Tooltip title={callTooltip('Inbound SMS', record.domestic_sms_only)}>
            <span><MessageOutlined /> {renderCapability(record.inbound_sms, record.domestic_sms_only)}</span>
          </Tooltip>
          <Tooltip title={callTooltip('Outgoing SMS', record.domestic_sms_only)}>
            <span><MessageOutlined rotate={135} /> {renderCapability(record.outgoing_sms, record.domestic_sms_only)}</span>
          </Tooltip>
        </div>
      ),
      width: 200
    },

    {
      key: 'monthly_fee',
      title: 'Monthly Fee',
      render: (_, { monthly_fee }) => <small>${monthly_fee}</small>,
      width: 100
    },

    {
      key: 'inbound_call_price',
      title: 'Call Price',
      render: (_, { inbound_call_price }) => <small>${inbound_call_price}</small>,
      width: 100
    },

    {
      key: 'inbound_sms_price',
      title: 'SMS Price',
      render: (_, { inbound_sms_price }) => <small>${inbound_sms_price}</small>,
      width: 100
    },

    {
      key: 'actions',
      title: 'Preview',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 10 }}>
          <Button
            onClick={() => openPricingModal(record.uid, record.number)}
            title="Plans"
            icon={DollarOutlined}
          />

          <Button
            onClick={() => openSmsModal(record.uid, record.number)}
            title="SMS"
            icon={MessageOutlined}
          />
        </div>
      ),
      width: 160
    },
  ];

  return (
    <>
      <Table
        rowKey='uid'
        columns={columns}
        dataSource={data}
      />

      <PricingPlansModal
        uid={pricingUid}
        number={modalNumber}
        onClose={() => setPricingUid(null)}
      />

      <SmsPackagesModal
        uid={smsUid}
        number={modalNumber}
        onClose={() => setSmsUid(null)}
      />
    </>
  )
}

export default _ListTable;
